import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { toast } from 'react-toastify';

let messaging = null;
let fcmToken = null;

/**
 * Initialize Firebase Messaging
 * @param {Object} firebaseApp - Firebase app instance
 */
export const initializeFCM = async (firebaseApp) => {
    try {
        if (!firebaseApp) {
            console.warn('Firebase app not provided for FCM initialization');
            return null;
        }

        // Check if browser supports required APIs
        if (!isNotificationSupported()) {
            console.warn('⚠️ FCM: Browser does not support notifications or service workers');
            return null;
        }

        // Check if service worker is available (required for FCM)
        if (!('serviceWorker' in navigator)) {
            console.warn('⚠️ FCM: Service Worker API not available in this browser');
            return null;
        }

        // Check if we're in a secure context (HTTPS or localhost)
        if (!window.isSecureContext && !window.location.hostname.includes('localhost')) {
            console.warn('⚠️ FCM: Requires secure context (HTTPS) or localhost');
            return null;
        }

        messaging = getMessaging(firebaseApp);
        
        // Request notification permission
        const permission = await Notification.requestPermission();
        
        if (permission === 'granted') {
            console.log('Notification permission granted');
            return messaging;
        } else {
            console.log('Notification permission denied');
            return null;
        }
    } catch (error) {
        // Handle specific Firebase errors gracefully
        if (error.code === 'messaging/unsupported-browser') {
            console.warn('⚠️ FCM: Browser does not support Firebase Messaging');
        } else {
            console.error('Error initializing FCM:', error);
        }
        return null;
    }
};

/**
 * Get FCM token for the current device
 * @param {string} vapidKey - VAPID key from Firebase console
 * @returns {Promise<string|null>} FCM token or null
 */
export const getFCMToken = async (vapidKey) => {
    try {
        if (!messaging) {
            console.warn('FCM not initialized');
            return null;
        }

        if (fcmToken) {
            return fcmToken;
        }

        const token = await getToken(messaging, { vapidKey });
        
        if (token) {
            console.log('FCM Token:', token);
            fcmToken = token;
            
            // Save token to localStorage for login API and socket connection
            localStorage.setItem('fcmToken', token);
            console.log('✅ FCM token saved to localStorage (will be sent via login API)');
            
            return token;
        } else {
            console.log('No registration token available');
            return null;
        }
    } catch (error) {
        console.error('Error getting FCM token:', error);
        return null;
    }
};

/**
 * Note: FCM token is now sent via login API
 * No need for separate save-token endpoint
 * Backend will receive and save token from login request
 */

/**
 * Setup foreground message handler
 * @param {Function} onNotificationReceived - Callback for notification received
 */
export const setupForegroundMessageHandler = (onNotificationReceived) => {
    if (!messaging) {
        console.warn('FCM not initialized');
        return;
    }

    onMessage(messaging, (payload) => {
        console.log('Foreground message received:', payload);
        
        const { notification, data } = payload;
        
        // Check if user is on a chat page - suppress all FCM toasts there
        const currentHash = window.location.hash || '';
        const isOnChatPage = currentHash.includes('patientchat') || currentHash.includes('doctorchat');

        // Skip FCM toast for chat messages - handled by socket global listener
        const isChatMessage = data?.type === 'CHAT_MESSAGE' || data?.roomId || data?.senderRole;
        if (isChatMessage) {
            // Only dispatch event, never show toast (socket handler covers this)
            window.dispatchEvent(new CustomEvent('chat-notification', { detail: data }));
        } else if (notification && !isOnChatPage) {
            // Show toast for non-chat notifications, but not when on chat page
            toast.info(notification.body || 'New notification', {
                autoClose: 5000,
                onClick: () => {
                    if (onNotificationReceived) {
                        onNotificationReceived(payload);
                    }
                }
            });
        }

        // Handle custom data
        if (data && data.type === 'support_ticket') {
            handleSupportTicketNotification(data, onNotificationReceived);
        }

        // Dispatch custom event for support ticket updates so pages can refresh
        if (data?.type === 'TICKET_REASSIGNED' || data?.type === 'support_ticket') {
            window.dispatchEvent(new CustomEvent('support-ticket-update', { detail: data }));
        }

        // The open-platform review was decided (issue #5): the doctor's
        // "Get verified" card re-reads its state if it is on screen.
        if (data?.type === 'DOCTOR_REVIEW') {
            window.dispatchEvent(new CustomEvent('doctor-review-update', { detail: data }));
        }
    });
};

/**
 * Handle support ticket notification
 * @param {Object} data - Notification data
 * @param {Function} callback - Callback function
 */
const handleSupportTicketNotification = (data, callback) => {
    const { ticketId, action, message } = data;
    
    console.log('Support ticket notification:', { ticketId, action, message });
    
    // Trigger callback with notification data
    if (callback) {
        callback({
            type: 'support_ticket',
            ticketId,
            action,
            message
        });
    }
};

/**
 * Navigate to support page on notification click
 * @param {Object} navigate - React Router navigate function
 * @param {string} userRole - User role (admin, doctor, patient)
 */
export const handleNotificationClick = (navigate, userRole) => {
    if (userRole === 'admin') {
        // Navigate to Admin Support -> Unassigned Tickets
        navigate('/support', { state: { tab: 'unassigned' } });
    } else {
        // Navigate to User Support -> Active Tickets
        navigate('/support', { state: { tab: 'active' } });
    }
};

/**
 * Request notification permission
 * @returns {Promise<string>} Permission status
 */
export const requestNotificationPermission = async () => {
    try {
        const permission = await Notification.requestPermission();
        return permission;
    } catch (error) {
        console.error('Error requesting notification permission:', error);
        return 'denied';
    }
};

/**
 * Check if notifications are supported
 * @returns {boolean}
 */
export const isNotificationSupported = () => {
    return 'Notification' in window && 'serviceWorker' in navigator;
};

/**
 * Get current notification permission status
 * @returns {string} Permission status
 */
export const getNotificationPermission = () => {
    if (!isNotificationSupported()) {
        return 'unsupported';
    }
    return Notification.permission;
};

/**
 * Get cached FCM token (synchronous)
 * @returns {string|null} Cached FCM token or null
 */
export const getCachedFCMToken = () => {
    return fcmToken;
};

export default {
    initializeFCM,
    getFCMToken,
    getCachedFCMToken,
    setupForegroundMessageHandler,
    handleNotificationClick,
    requestNotificationPermission,
    isNotificationSupported,
    getNotificationPermission
};

